import type { AdPointsEntry } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, Loader2 } from "lucide-react";
import { useState } from "react";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { adminAds, adsAvailable } from "@/pages/ad-campaigns-api";
import { useAuth } from "@/store";
const KIND: Record<AdPointsEntry["kind"], string> = {
  grant: "Начисление",
  spend: "Оплата рекламы",
  refund: "Возврат",
  expire: "Сгорание",
  adjust: "Корректировка"
};
const SOURCE: Record<string, string> = {
  welcome: "приветственные",
  referral: "за привлечённый магазин",
  adjust: "вручную"
};

/** Пояснение к записи: кампания, причина, срок партии, кто правил. */
const noteOf = (e: AdPointsEntry): string => [e.campaignTitle ? `«${e.campaignTitle}»` : null, e.comment && e.comment !== KIND[e.kind] && e.comment !== "Оплата рекламы" ? e.comment : null, e.expiresAt ? `сгорят ${new Date(e.expiresAt).toLocaleDateString("ru-RU")}` : null, e.actorName || null].filter(Boolean).join(" · ");
const points = (value: number) => value.toLocaleString("ru-RU", {
  maximumFractionDigits: 2
});
const date = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");
const dateTime = (iso: string) => new Date(iso).toLocaleString("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});

/**
 * «Рекламные баллы» магазина в консоли (PDF заказчика п.4, 27.10.2026):
 * баланс и партии со сроками, журнал начислений, оплат рекламы и сгораний,
 * ручная корректировка с причиной. Смотреть — с карточкой магазина;
 * корректировать — с правом финансовых действий (action.finances.manage).
 * Корректировка попадает в журнал действий консоли с причиной.
 */
export function SellerAdPoints({
  userId
}: {
  userId: string;
}) {
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canAdjust = hasAdminPermission(user, "action.finances.manage");
  const key = ["admin-ads", "points", userId];
  const {
    data,
    error,
    isLoading
  } = useQuery({
    queryKey: key,
    queryFn: () => adminAds.points(userId),
    enabled: adsAvailable && Boolean(userId)
  });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  if (!adsAvailable) return null;
  const delta = Number(amount.replace(/\s/g, "").replace(",", "."));
  const valid = Number.isFinite(delta) && delta !== 0 && reason.trim().length >= 3;
  const save = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const next = await adminAds.adjustPoints(userId, delta, reason.trim());
      queryClient.setQueryData(key, next);
      setEditing(false);
      setAmount("");
      setReason("");
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const history = data?.history ?? [];
  const visible = showAll ? history : history.slice(0, 8);
  return <Card data-testid="seller-ad-points">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Gift className="size-4" />
          Рекламные баллы
        </CardTitle>
        {canAdjust && !editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="seller-ad-points-adjust">
            Корректировать
          </Button>}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {error && <p className="text-destructive">{(error as Error).message}</p>}
        {isLoading && <p className="text-muted-foreground">Загрузка…</p>}
        {data && <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <p>
              <span className="text-2xl font-semibold" data-testid="seller-ad-points-balance">
                {points(data.balance)}
              </span>{" "}
              баллов{" "}
              <span className="text-muted-foreground">(1 балл = 1 руб., только на рекламу)</span>
            </p>
            {data.nextExpiry && <p className="text-muted-foreground">
                Ближайшее сгорание: {points(data.nextExpiry.amount)} - {date(data.nextExpiry.at)}
              </p>}
          </div>}
        {data && data.lots.length > 0 && <ul className="flex flex-col gap-1 rounded-md border p-3">
            {data.lots.map(lot => <li key={lot.id} className="flex flex-wrap justify-between gap-2">
                <span>
                  {points(lot.remaining)} из {points(lot.amount)} ·{" "}
                  {SOURCE[lot.source] ?? lot.source}
                  {lot.comment && <span className="text-muted-foreground"> · {lot.comment}</span>}
                </span>
                <span className="text-muted-foreground">
                  с {date(lot.grantedAt)} до {date(lot.expiresAt)}
                </span>
              </li>)}
          </ul>}

        {editing && <div className="flex flex-col gap-2 rounded-md border p-3" data-testid="seller-ad-points-form">
            <Input inputMode="decimal" value={amount} onChange={e_0 => setAmount(e_0.target.value)} placeholder="Сумма: 500 - начислить, -500 - списать" data-testid="seller-ad-points-amount" />
            <Input value={reason} onChange={e_1 => setReason(e_1.target.value)} placeholder="Причина (попадёт в журнал действий)" data-testid="seller-ad-points-reason" />
            <p className="text-xs text-muted-foreground">
              Начисление - новая партия со сроком 30 дней. Списание - сначала из партий, которые
              сгорают раньше, не больше остатка.
            </p>
            {actionError && <p className="text-destructive">{actionError}</p>}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy || !valid} onClick={() => void save()} data-testid="seller-ad-points-save">
                {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                Сохранить
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
                Отмена
              </Button>
            </div>
          </div>}

        {data && <div className="flex flex-col gap-1" data-testid="seller-ad-points-history">
            <p className="font-medium">История</p>
            {history.length === 0 ? <p className="text-muted-foreground">Начислений не было.</p> : <>
                {/* Телефон: списком, без горизонтальной прокрутки. */}
                <ul className="flex flex-col sm:hidden">
                  {visible.map(e_2 => <li key={e_2.id} className="flex flex-col gap-0.5 border-t py-2">
                      <div className="flex justify-between gap-3">
                        <span className="font-medium">{KIND[e_2.kind] ?? e_2.kind}</span>
                        <span className={e_2.delta < 0 ? "text-destructive" : "text-emerald-700"}>
                          {e_2.delta > 0 ? "+" : ""}
                          {points(e_2.delta)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                        <span>{dateTime(e_2.createdAt)}</span>
                        <span>остаток {points(e_2.balanceAfter)}</span>
                      </div>
                      {noteOf(e_2) && <p className="text-xs text-muted-foreground">{noteOf(e_2)}</p>}
                    </li>)}
                </ul>
                <div className="hidden overflow-x-auto sm:block">
                  <table className="w-full min-w-[520px] text-left">
                    <thead className="text-xs text-muted-foreground">
                      <tr>
                        <th className="py-1 pr-3 font-normal">Когда</th>
                        <th className="py-1 pr-3 font-normal">Операция</th>
                        <th className="py-1 pr-3 text-right font-normal">Баллы</th>
                        <th className="py-1 pr-3 text-right font-normal">Остаток</th>
                        <th className="py-1 font-normal">Комментарий</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(e_3 => <tr key={e_3.id} className="border-t align-top">
                          <td className="py-1.5 pr-3 whitespace-nowrap">{dateTime(e_3.createdAt)}</td>
                          <td className="py-1.5 pr-3">{KIND[e_3.kind] ?? e_3.kind}</td>
                          <td className={e_3.delta < 0 ? "py-1.5 pr-3 text-right whitespace-nowrap text-destructive" : "py-1.5 pr-3 text-right whitespace-nowrap text-emerald-700"}>
                            {e_3.delta > 0 ? "+" : ""}
                            {points(e_3.delta)}
                          </td>
                          <td className="py-1.5 pr-3 text-right whitespace-nowrap">
                            {points(e_3.balanceAfter)}
                          </td>
                          <td className="py-1.5 text-muted-foreground">{noteOf(e_3)}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </>}
            {history.length > 8 && <Button variant="ghost" size="sm" className="self-start" onClick={() => setShowAll(v => !v)}>
                {showAll ? "Свернуть" : `Показать все (${history.length})`}
              </Button>}
          </div>}
      </CardContent>
    </Card>;
}